import React, { useEffect, useState } from "react";
import {
    Modal,
    Row,
    Col,
    Card,
    Typography,
    Descriptions,
    Tag,
    Table,
    Image,
    Button,
    Input,
    Select,
    Checkbox,
    Tooltip,
    Spin,
    message,
    Space,
} from "antd";
import {
    NumberOutlined,
    ShopOutlined,
    FileTextOutlined,
    ClockCircleOutlined,
    UserOutlined,
    PhoneOutlined,
    HomeOutlined,
    MessageOutlined,
    CarOutlined,
    BarcodeOutlined,
    SaveOutlined,
    CaretRightOutlined,
    RightCircleOutlined,
    SyncOutlined,
} from "@ant-design/icons";
import { RATE_EURO_TO_USD, statusOrderMap } from "../../../constants/constants";
import SelectStatusCustom from "../../../components/SelectStatusCustom";
import { consoleLogUtil } from "../../../utils/consoleLogUtil";
import { useSelector } from "react-redux";
import { getTransactionByOrderId, updateOrderItem, updateSellerNoteByOrdreId } from "../../../services/apis";
import { formatNumber } from "../../../utils/funcHelper";

const { Title, Text } = Typography;

const OrderDetailModal = ({ visible, onClose, onSave, order, providers, printers, skus }) => {
    const [status, setStatus] = useState(order?.status || "waiting");
    const [tracking, setTracking] = useState("");
    const [provider, setProvider] = useState("");
    const [printer, setPrinter] = useState("");
    const [selectedSkus, setSelectedSkus] = useState({});
    const [loadingRows, setLoadingRows] = useState({});
    const userReducer = useSelector((state) => state?.userReducer?.data);
    const [sellerNote, setSellerNote] = useState(order?.order_info?.seller_note || "");
    const [isSavingNote, setIsSavingNote] = useState(false);
    const [isSkipAutoUpdateTiktok, setIsSkipAutoUpdateTiktok] = useState(order?.tracking_info?.isSkipAutoUpdateTiktok === 1);
    const [est_settlement_amount, setEstSettlementAmount] = useState();
    const [loading, setLoading] = useState(false);

    const columns = [
        {
            title: "Image",
            dataIndex: "sku_image",
            key: "sku_image",
            render: (src) => <Image src={src} alt="product" width={50} />,
            width: 80,
        },
        {
            title: "Product Name",
            dataIndex: "product_name",
            key: "product_name",
            render: (text) => <Tooltip title={text}><Text>{text?.length > 30 ? text.substring(0, 30).concat("...") : text}</Text></Tooltip>,
        },
        {
            title: "Price",
            dataIndex: "sale_price",
            key: "sale_price",
            render: (price) => <Text strong>£{price}</Text>,
            width: 100,
        },
        {
            title: "Quantity",
            dataIndex: "quantity",
            key: "quantity",
            align: "center",
            render: (quantity) => <Text strong>{quantity}</Text>,
            width: 50,
        },
        {
            title: "Base cost",
            dataIndex: "base_cost",
            key: "base_cost",
            align: "center",
            render: (base_cost) => <Text strong>{base_cost}</Text>,
            width: 50,
        },
        {
            title: "Total cost",
            dataIndex: "total_cost",
            key: "total_cost",
            align: "center",
            render: (total_cost) => <Text strong>{total_cost}</Text>,
            width: 50,
        },
        {
            title: "Chọn SKU", // Cột mới
            dataIndex: "sku_print_id",
            key: "sku_print_id",
            width: 300,
            render: (text, record, index) => (
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Select
                        placeholder="Select sku"
                        value={selectedSkus[record.id] || text}
                        onChange={(id) => handleChangeSKU(id, record)}
                        style={{ width: 380, textAlign: "left" }}
                        showSearch
                        filterOption={(input, option) => {
                            const { sku_code, sku_name } = option.props;
                            return (
                                sku_code.toLowerCase().includes(input.toLowerCase()) ||
                                sku_name.toLowerCase().includes(input.toLowerCase())
                            );
                        }}
                    >
                        {skus && skus.length > 0 ? (
                            skus.map((sku) => (
                                <Select.Option
                                    key={sku?.id}
                                    value={sku?.id}
                                    sku_code={sku?.sku_code}
                                    sku_name={sku?.sku_name}
                                >
                                    <div style={{ display: "flex" }}>
                                        <span>{sku?.sku_code}</span>
                                        &nbsp; | &nbsp;
                                        <span>{sku?.sku_name}</span>
                                    </div>
                                </Select.Option>
                            ))
                        ) : (
                            <Select.Option value="" disabled>
                                No SKUs available
                            </Select.Option>
                        )}
                    </Select>
                    {loadingRows[record.id] ? <Spin size="small" /> : null} {/* Hiển thị loading nếu hàng đang xử lý */}
                </div>
            ),
        },
        {
            title: "Variant",
            dataIndex: "sku_name",
            key: "sku_name",
            width: 120,
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 120,
            render: (st) => (
                <Tag color={statusOrderMap[st]?.color || "blue"} icon={statusOrderMap[st]?.icon}>
                    {statusOrderMap[st]?.text || st}
                </Tag>
            ),
        },
    ];
    const handleChangeSKU = async (id, record) => {
        setSelectedSkus((prev) => ({
            ...prev,
            [record.id]: id, // Lưu giá trị `id` của `Select` theo `rowKey`
        }));
        setLoadingRows((prev) => ({
            ...prev,
            [record.id]: true,
        }));

        try {
            // Gọi API để cập nhật dữ liệu
            await updateSkuForOrder(record, id); // Gọi API update order item
            setSelectedSkus((prev) => ({
                ...prev,
                [record.id]: id, // Lưu giá trị `id` của `Select` theo `rowKey`
            }));
        } catch (error) {
            console.error("Failed to update SKU:", error);
        } finally {
            // Tắt trạng thái loading cho hàng hiện tại
            setLoadingRows((prev) => ({
                ...prev,
                [record.id]: false,
            }));
        }
    }

    const updateSkuForOrder = async (data, sku_print_id) => {
        try {
            const params = {
                id: data?.id,
                sku_print_id: sku_print_id,
                user_id: userReducer?.user_info?.id,
            }
            const res = await updateOrderItem(params);
            if (res && res.status === 1) {
                message.success('Update item success');
                const base_cost = res?.data?.base_cost || 0;
                const total_cost = res?.data?.total_cost || 0;
                // Cập nhật lại dữ liệu hiển thị trong bảng
                order.items = order.items.map(item => {
                    if (item.id === data.id) {
                        return {
                            ...item,
                            sku_print_id: sku_print_id,
                            base_cost: base_cost,
                            total_cost: total_cost,
                        };
                    }
                    return item;
                });
            } else {
                message.error('Update item failed');
            }
        } catch (error) {
            console.log('error', error);
        }
    };
    const handleSaveSellerNote = async () => {
        if (isSavingNote) return; // Ngăn chặn nhiều lần nhấn
        setIsSavingNote(true);
        try {
            const params = {
                order_id: order?.order_info?.order_id,
                note: sellerNote,
                user_id: userReducer?.user_info?.id,
            };

            // Gọi API để cập nhật seller note
            const res = await updateSellerNoteByOrdreId(params);
            if (res && res.status === 1) {
                message.success("Cập nhật seller note thành công.");
            } else {
                message.error("Cập nhật seller note thất bại.");
            }
        } catch (error) {
            console.error("Error updating seller note:", error);
            message.error("Có lỗi xảy ra khi cập nhật seller note.");
        } finally {
            setIsSavingNote(false);
        }
    };
    const handleOnSave = (data) => {
        if (onSave) {
            onClose();
            onSave(data);
        }
    }

    const handleResyncEstSettlementAmount = async () => {
        try {
            const params = {
                order_id: order?.order_info?.order_id,
                shop_id: order?.order_info?.shop_id,
            };
            setLoading(true);
            // Gọi API để cập nhật seller note
            const res = await getTransactionByOrderId(params);
            consoleLogUtil("Resync est settlement amount response:", res);
            if (res && res.status === 1) {
                message.success("Cập nhật số tiền ước tính thành công.");
                setEstSettlementAmount(res?.data?.data || 'N/A');
            } else {
                message.error("Cập nhật số tiền ước tính thất bại.");
            }
        } catch (error) {
            console.error("Error updating est settlement amount:", error);
            message.error("Có lỗi xảy ra khi cập nhật số tiền ước tính.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        setStatus(order?.order_info?.status || "waiting");
        setTracking(order?.tracking_info?.tracking_number || "");
        setProvider(order?.tracking_info?.provider_id || "");
        setPrinter(order?.order_info?.supplier_id || "");
        setSellerNote(order?.order_info?.seller_note || "");
        setEstSettlementAmount(order?.order_summary?.est_settlement_amount || 'N/A');
        setIsSkipAutoUpdateTiktok(order?.tracking_info?.isSkipAutoUpdateTiktok === 1);
        // Khởi tạo selectedSkus từ order items
        const initialSelectedSkus = {};
        order?.items?.forEach(item => {
            initialSelectedSkus[item.id] = item.sku_print_id || null;
        }
        );
        setSelectedSkus(initialSelectedSkus);
    }, [order, providers, printers, skus]);

    return (
        <Modal
            title="Order Details"
            open={visible}
            onCancel={onClose}
            width={'90%'}
            bodyStyle={{ backgroundColor: "#f5f5f5", padding: 16 }}
            className="custom-modal"
            footer={[
                <Button key="cancel" onClick={onClose}>
                    Cancel
                </Button>,
                <Button
                    key="save"
                    type="primary"
                    onClick={() => handleOnSave({ ...order, status, tracking, provider, supplier_id: printer, isSkipAutoUpdateTiktok: isSkipAutoUpdateTiktok ? 1 : 0 })}
                >
                    Save
                </Button>,
            ]}
        >
            <Row gutter={16}>
                {/* CỘT TRÁI */}
                <Col span={16}>
                    <Card size="small" style={{ marginBottom: 16, borderRadius: 8 }}>
                        <Title level={5}>Order Information</Title>
                        <Descriptions
                            column={1}
                            bordered
                            size="middle"
                            labelStyle={{ width: "30%", fontWeight: 500, background: "#fafafa" }}
                            contentStyle={{ textAlign: "right", padding: "8px 12px" }}
                        >
                            <Descriptions.Item label={<><NumberOutlined /> Order ID</>}>
                                {order?.order_info?.order_id || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label={<><NumberOutlined /> Tiktok Status</>}>
                                {order?.order_info?.tiktok_status || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label={<><FileTextOutlined /> Status</>}>
                                <SelectStatusCustom
                                    status={status}
                                    onChange={setStatus}
                                    customStyle={{ width: 150 }}
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label={<><FileTextOutlined /> Printer</>}>
                                <Select
                                    placeholder="Select printer"
                                    allowClear
                                    value={printer}
                                    onChange={setPrinter}
                                    style={{ width: 150, textAlign: 'left' }}
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.props.children.toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    {printers && printers.length > 0 ? (
                                        printers.map((print) => (
                                            <Select.Option key={print?.id} value={print?.id}>
                                                {print?.name}
                                            </Select.Option>
                                        ))
                                    ) : (
                                        <Select.Option value="" disabled>
                                            No printers
                                        </Select.Option>
                                    )}
                                </Select>
                            </Descriptions.Item>
                            {/* Nhập sku */}


                            <Descriptions.Item label={<><ShopOutlined /> Shop Name</>}>
                                {order?.order_info?.shop_name || 'N/A'}
                            </Descriptions.Item>
                            {/* <Descriptions.Item label={<><FileTextOutlined /> Seller Note</>}>
                                {order?.order_info?.seller_note || 'N/A'}
                            </Descriptions.Item> */}
                            <Descriptions.Item label={<><FileTextOutlined /> Seller Note</>}>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <Input
                                        placeholder="Enter seller note"
                                        value={sellerNote}
                                        onChange={(e) => setSellerNote(e.target.value)} // Cập nhật giá trị khi người dùng nhập
                                        style={{ flex: 1 }}
                                    />
                                    <Button
                                        loading={isSavingNote}
                                        disabled={isSavingNote}
                                        danger
                                        icon={<SaveOutlined />}
                                        onClick={handleSaveSellerNote} // Hàm xử lý khi nhấn nút Save
                                    >
                                        Save note
                                    </Button>
                                </div>
                            </Descriptions.Item>
                            <Descriptions.Item label={<><ClockCircleOutlined /> Created At</>}>
                                {order?.order_info?.createdAt || 'N/A'}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>

                    {/* Items */}
                    <Card size="small" style={{ borderRadius: 8 }}>
                        <Title level={5}>Items ({order?.items?.length || 0})</Title>
                        <Table
                            columns={columns}
                            dataSource={order?.items || []}
                            pagination={false}
                            rowKey={(record) => record.id}
                            scroll={{ x: true }}
                        />
                    </Card>
                </Col>

                {/* CỘT PHẢI */}
                <Col span={8}>
                    <Card size="small" style={{ marginBottom: 16, borderRadius: 8 }}>
                        <Title level={5}>Order Summary</Title>
                        <p><RightCircleOutlined /> Subtotal: £{order?.order_summary?.subtotal || '0'}</p>
                        <p><RightCircleOutlined /> Shipping Fee: £{order?.order_summary?.shipping_fee || '0'}</p>
                        <Title level={5}>Total: £{order?.order_summary?.total || '0'}</Title>
                        <Space>
                            <Title level={4} style={{ color: 'blue' }}>Est earned: £ {est_settlement_amount} {est_settlement_amount ? ` => ${formatNumber(est_settlement_amount * RATE_EURO_TO_USD, 2)}$` : ''}</Title>
                            <Tooltip
                                title="Cập nhật lại số tiền ước tính"
                            >
                                <Button
                                    danger
                                    icon={<SyncOutlined />}
                                    style={{ marginLeft: 8, height: 28 }}
                                    onClick={() => handleResyncEstSettlementAmount()}
                                    loading={loading}
                                />
                            </Tooltip>

                        </Space>
                    </Card>

                    <Card size="small" style={{ borderRadius: 8, marginBottom: 16 }}>
                        <Title level={5}>Customer Information</Title>
                        <p><UserOutlined /> {order?.customer_info?.name || 'N/A'}</p>
                        <p><HomeOutlined /> {order?.customer_info?.full_address || 'N/A'}</p>
                        <p><PhoneOutlined /> {order?.customer_info?.phone || 'N/A'}</p>
                        <p><MessageOutlined /> {order?.customer_info?.buyer_message || 'N/A'}</p>
                    </Card>
                    {/* Card Shipping */}
                    <Card size="small" style={{ borderRadius: 8 }}>
                        <Title level={5}>Order Shipping Information</Title>
                        <div style={{ marginBottom: 16 }}>
                            <Text strong>
                                <BarcodeOutlined /> Tracking Number:
                            </Text>
                            <Input
                                placeholder="Enter tracking number"
                                value={tracking}
                                onChange={(e) => setTracking(e.target.value)}
                                style={{ marginTop: 8 }}
                            />
                        </div>
                        <div>
                            <Text strong>
                                <CarOutlined /> Provider:
                            </Text>
                            <Select
                                placeholder="Select provider"
                                value={provider}
                                onChange={setProvider}
                                style={{ width: "100%", marginTop: 8 }}
                                showSearch
                                filterOption={(input, option) =>
                                    option.props.children.toLowerCase().includes(input.toLowerCase())
                                }
                            >
                                {providers && providers.length > 0 ? (
                                    providers.map((prov) => (
                                        <Select.Option key={prov?.provider_id} value={prov?.provider_id}>
                                            {prov?.name}
                                        </Select.Option>
                                    ))
                                ) : (
                                    <Select.Option value="" disabled>
                                        No providers available
                                    </Select.Option>
                                )}
                            </Select>
                        </div>
                        {
                            order?.tracking_info?.isUpdatedTiktok === 1 ?
                                <Checkbox
                                    checked
                                    style={{ color: '#1890ff', marginTop: 12 }}
                                >
                                    Đã gửi tracking number cho tiktok shop
                                </Checkbox> : null

                        }
                        <Checkbox
                            checked={isSkipAutoUpdateTiktok}
                            onChange={(e) => setIsSkipAutoUpdateTiktok(e.target.checked)}
                            style={{ color: 'red', marginTop: 12 }}

                        >
                            Bỏ update tracking tự động
                        </Checkbox>
                    </Card>
                    <Card size="small" style={{ marginTop: 16, borderRadius: 8 }}>
                        <Title level={5}>Last Update</Title>
                        <p>
                            <UserOutlined /> Updated By: {order?.order_info?.updatedBy_fullname || 'N/A'}
                        </p>
                        <p>
                            <ClockCircleOutlined /> Updated At: {order?.order_info?.updatedAt || 'N/A'}
                        </p>
                    </Card>
                </Col>
            </Row>
        </Modal>
    );
};

export default OrderDetailModal;
